// src/main.ts
// App shell / bootstrap (spec §10 phase 2-3 + phase 5 polish). Reads the resume level, generates it,
// wires the canvas renderer + animation loop + input + HUD, and runs the core loop:
//   move → animate → commit → win → advance (persist currentLevel + star) — with undo/reset always
//   available. Phase 5 adds: synthesized SFX (slide/exit/win, gated by the sound setting), a confetti
//   win burst (skipped under reduced motion), a first-run visual tutorial, and a subtle Hint button.
// Hard rules (§0): no timer, no lives, no fail states, ZERO runtime network calls.

import './style.css';
// Self-hosted rounded display font (bundled into dist by Vite — no runtime network, §0).
import '@fontsource/fredoka/400.css';
import '@fontsource/fredoka/500.css';
import '@fontsource/fredoka/600.css';
import '@fontsource/fredoka/700.css';

import type { GeneratedLevel } from './gen/generator';
import { generateLevel } from './gen/generator';
import { mulberry32 } from './gen/prng';
import type { GameState } from './game/rules';
import {
  applyExitAt,
  applyMove,
  applyRelocate,
  getBlock,
  isSolved,
  reachableAnchors,
} from './game/rules';
import { applyHay, revealOnSlide } from './game/obstacles';
import type { History } from './game/moves';
import { canUndo, commit, initHistory, moveCount, reset, undo } from './game/moves';
import type { BlockId, Cell, Direction } from './game/types';
import { DELTAS } from './game/types';

import type { BlockAnim } from './render/canvas';
import { CanvasView } from './render/canvas';
import { Animator } from './render/animate';
import { Controls } from './input/controls';
import { Hud } from './ui/hud';
import { Sfx } from './audio/sfx';
import { Confetti } from './render/confetti';
import { Tutorial } from './ui/tutorial';
import { nextHintMove } from './gen/solver';

import type { Settings } from './state/storage';
import {
  loadCurrentLevel,
  loadSettings,
  loadTutorialSeen,
  recordLevelResult,
  requestPersistentStorage,
  resetAllProgress,
  saveCurrentLevel,
  saveSettings,
  saveTutorialSeen,
} from './state/storage';

const AUTO_ADVANCE_MS = 1600;
const CELEBRATE_MS = 900;

/** How long a Hint indicator lingers before auto-clearing. */
const HINT_MS = 2200;
/** Small solver budget for the Hint button so a tap never janks the UI (§4.1). */
const HINT_BUDGET = { maxDepth: 50, maxNodes: 30000, maxMillis: 60 } as const;

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

class App {
  private level: GeneratedLevel;
  private history: History;
  private settings: Settings;

  private selectedBlockId: BlockId | null = null;
  private focusVisible = false;
  private currentAnim: BlockAnim | null = null;
  private won = false;
  private nextTimer: number | null = null;
  /** Bumped on every level (re)load; lets an in-flight move drop its stale post-animation commit. */
  private loadGeneration = 0;

  // ---- Hint state (§4.1) ----
  private hint: { readonly blockId: BlockId; readonly dir: Direction } | null = null;
  private hintStart = 0;
  private hintRaf: number | null = null;
  private hintClearTimer: number | null = null;

  // ---- audio priming ----
  private audioPrimed = false;
  private readonly onFirstGesture: () => void;

  private readonly view: CanvasView;
  private readonly animator: Animator;
  private readonly controls: Controls;
  private readonly hud: Hud;
  private readonly sfx: Sfx;
  private readonly confetti: Confetti | null;
  private tutorial: Tutorial | null = null;
  private readonly boardWrap: HTMLElement | null;

  constructor(canvas: HTMLCanvasElement) {
    this.settings = loadSettings();
    const startLevel = loadCurrentLevel();
    this.level = safeGenerate(startLevel);
    this.history = initHistory(this.startState(this.level));
    saveCurrentLevel(this.level.levelIndex); // keep stored index consistent if generation nudged forward
    this.boardWrap = document.getElementById('board-wrap');

    this.view = new CanvasView(canvas);
    this.animator = new Animator((anim) => {
      this.currentAnim = anim;
      this.render();
    });
    this.controls = new Controls(canvas, this.view, {
      getState: () => this.history.present,
      getSelected: () => this.selectedBlockId,
      isBusy: () => this.animator.running || this.won,
      onSelect: (id) => this.select(id),
      onMove: (id, dir, distance) => {
        void this.requestMove(id, dir, distance);
      },
      getReach: (id) => reachableAnchors(this.history.present, id),
      onDragPreview: (id, anchor) => this.previewDrag(id, anchor),
      onDrop: (id, anchor, exit) => this.dropPiece(id, anchor, exit),
      onUndo: () => this.doUndo(),
      onReset: () => this.doReset(),
      setKeyboardActive: (active) => this.setFocusVisible(active),
    });
    this.hud = new Hud(
      {
        onUndo: () => this.doUndo(),
        onReset: () => this.doReset(),
        onNext: () => this.advance(),
        onHint: () => this.requestHint(),
        onSettingsChange: (s) => this.applySettings(s),
        onResetProgress: () => this.doResetProgress(),
      },
      this.settings,
    );

    this.sfx = new Sfx(this.settings.sound);
    this.confetti = this.boardWrap !== null ? new Confetti(this.boardWrap) : null;

    // Resume the AudioContext on the first real user gesture (autoplay policy).
    this.onFirstGesture = () => this.handleFirstGesture();
    window.addEventListener('pointerdown', this.onFirstGesture, true);
    window.addEventListener('keydown', this.onFirstGesture, true);
    window.addEventListener('touchstart', this.onFirstGesture, true);

    this.hud.setLevel(this.level.levelIndex);
    this.render();
    this.maybeShowTutorial();
  }

  dispose(): void {
    this.clearNextTimer();
    this.clearHint();
    this.removeGestureListeners();
    this.controls.dispose();
    this.view.dispose();
    if (this.confetti !== null) {
      this.confetti.dispose();
    }
    if (this.tutorial !== null) {
      this.tutorial.dispose();
    }
    this.sfx.dispose();
  }

  // ---- rendering ----

  private render(): void {
    this.view.render({
      state: this.history.present,
      selectedBlockId: this.selectedBlockId,
      focusVisible: this.focusVisible,
      anim: this.currentAnim,
      hint: this.hint,
      hintPulse: this.hint === null ? 0 : this.hintPulseValue(),
    });
    this.hud.setUndoEnabled(canUndo(this.history));
    this.hud.setMoves(moveCount(this.history), this.level.par);
    this.hud.setStar(moveCount(this.history) <= this.level.par);
  }

  /** Build a level's starting state, layering hay cover per its difficulty (deterministic by seed). */
  private startState(level: GeneratedLevel): GameState {
    return applyHay(level.initialState, level.difficulty, mulberry32((level.seed >>> 0) ^ 0x9e3779b9));
  }

  // ---- selection / focus ----

  private select(id: BlockId | null): void {
    this.clearHint();
    this.selectedBlockId = id;
    this.render();
  }

  private setFocusVisible(active: boolean): void {
    if (this.focusVisible === active) {
      return;
    }
    this.focusVisible = active;
    this.render();
  }

  // ---- the move loop ----

  private async requestMove(id: BlockId, dir: Direction, distance: number): Promise<void> {
    // Any move dismisses an active hint indicator.
    if (this.hint !== null) {
      this.clearHint();
      this.render();
    }
    if (this.animator.running || this.won) {
      return;
    }
    const present = this.history.present;
    if (getBlock(present, id) === undefined) {
      return;
    }
    // Apply the requested distance; the engine clamps to what is actually free.
    const mr = applyMove(present, { blockId: id, dir, distance });
    if (mr.result.kind === 'noop') {
      return; // §2.3: no animation, no history.
    }

    const delta = DELTAS[dir];
    const reduced = this.reducedMotion();
    const gen = this.loadGeneration;

    if (mr.result.kind === 'slid') {
      await this.animator.slide(id, delta, mr.result.distance, reduced);
      if (gen !== this.loadGeneration) {
        return; // level was swapped mid-animation (reset/advance) — drop the stale commit
      }
      this.currentAnim = null;
      this.history = commit(this.history, revealOnSlide(mr.state, id)); // hay: peel one layer on a settled slide
      this.render();
      this.sfx.slide(); // soft clack on settle (§5.4)
      return;
    }

    // 'exited'
    await this.animator.exit(id, delta, mr.result.distance, reduced);
    if (gen !== this.loadGeneration) {
      return; // level was swapped mid-animation — drop the stale commit
    }
    this.currentAnim = null;
    this.history = commit(this.history, mr.state);
    if (this.selectedBlockId === id) {
      this.selectedBlockId = null;
    }
    this.render();
    this.sfx.exit(); // gentle pop as the block clears its gate (§5.4)
    if (isSolved(this.history.present)) {
      this.onWin();
    }
  }

  // ---- free-roam drag (pointer): move a piece anywhere it can reach via a clear path ----

  /** Live preview while dragging: render `id` ghosted at `anchor` (null clears the preview). */
  private previewDrag(id: BlockId, anchor: Cell | null): void {
    if (this.animator.running || this.won) {
      return;
    }
    if (anchor === null) {
      this.currentAnim = null;
      this.render();
      return;
    }
    const block = getBlock(this.history.present, id);
    if (block === undefined) {
      return;
    }
    this.currentAnim = {
      blockId: id,
      offsetRow: anchor.row - block.anchor.row,
      offsetCol: anchor.col - block.anchor.col,
      alpha: 1,
      scale: 1.06, // a slight lift while carried
    };
    this.render();
  }

  /** Drop a dragged piece: relocate it to `anchor`, or exit it through its gate when `exit`. */
  private dropPiece(id: BlockId, anchor: Cell, exit: boolean): void {
    if (this.hint !== null) {
      this.clearHint();
    }
    if (this.animator.running || this.won) {
      this.currentAnim = null;
      this.render();
      return;
    }
    const present = this.history.present;
    if (getBlock(present, id) === undefined) {
      return;
    }
    const mr = exit ? applyExitAt(present, id, anchor) : applyRelocate(present, id, anchor);
    this.currentAnim = null;
    if (mr.result.kind === 'noop') {
      this.render(); // snap the preview back; nothing committed
      return;
    }
    if (mr.result.kind === 'exited') {
      this.history = commit(this.history, mr.state);
      if (this.selectedBlockId === id) {
        this.selectedBlockId = null;
      }
      this.render();
      this.sfx.exit();
      if (isSolved(this.history.present)) {
        this.onWin();
      }
      return;
    }
    // relocated ('slid'): commit at the dropped position, peeling any hay on settle.
    this.history = commit(this.history, revealOnSlide(mr.state, id));
    this.render();
    this.sfx.slide();
  }

  // ---- win / advance ----

  private onWin(): void {
    this.won = true;
    this.clearHint();
    const earned = moveCount(this.history) <= this.level.par;
    if (this.level.levelIndex >= 1) {
      recordLevelResult(this.level.levelIndex, earned ? 1 : 0);
    }
    this.hud.setStar(earned);
    this.celebrate();
    if (this.confetti !== null) {
      this.confetti.burst(this.reducedMotion());
    }
    this.sfx.win();
    this.hud.showNext(true);
    this.clearNextTimer();
    this.nextTimer = window.setTimeout(() => this.advance(), AUTO_ADVANCE_MS);
  }

  private advance(): void {
    this.clearNextTimer();
    if (!this.won) {
      return;
    }
    this.loadLevel(this.level.levelIndex + 1); // loadLevel persists the actual generated index
  }

  private loadLevel(index: number): void {
    this.loadGeneration++; // invalidate any in-flight move's pending post-animation commit
    this.clearNextTimer();
    this.clearHint();
    this.won = false;
    this.selectedBlockId = null;
    this.currentAnim = null;
    this.animator.cancel();
    this.hud.showNext(false);
    this.level = safeGenerate(index);
    this.history = initHistory(this.startState(this.level));
    this.hud.setLevel(this.level.levelIndex);
    saveCurrentLevel(this.level.levelIndex); // persist the ACTUAL generated index, not the requested one (§7)
    this.render();
  }

  // ---- undo / reset (always available, §2.5) ----

  private doUndo(): void {
    if (this.animator.running) {
      return;
    }
    this.clearHint();
    // Undoing right after a win cancels the auto-advance and un-exits the last block.
    if (this.won) {
      this.won = false;
      this.clearNextTimer();
      this.hud.showNext(false);
    }
    if (!canUndo(this.history)) {
      this.render();
      return;
    }
    this.history = undo(this.history);
    this.selectedBlockId = null;
    this.currentAnim = null;
    this.render();
  }

  private doReset(): void {
    if (this.animator.running) {
      return;
    }
    this.clearHint();
    if (this.won) {
      this.won = false;
      this.clearNextTimer();
      this.hud.showNext(false);
    }
    this.history = reset(this.history);
    this.selectedBlockId = null;
    this.currentAnim = null;
    this.render();
  }

  // ---- hint (§4.1) ----

  private requestHint(): void {
    if (this.animator.running || this.won) {
      return;
    }
    const present = this.history.present;
    if (isSolved(present)) {
      return;
    }
    const move = nextHintMove(present, HINT_BUDGET);
    if (move === null) {
      return; // no hint available within budget — do nothing gracefully
    }
    this.selectedBlockId = null; // keep the board calm: show only the hint cue
    this.hint = { blockId: move.blockId, dir: move.dir };
    this.hintStart = now();
    this.startHintLoop();
  }

  /** Gentle 0..1 pulse for the hint cue; steady (1) under reduced motion. */
  private hintPulseValue(): number {
    if (this.reducedMotion()) {
      return 1;
    }
    const elapsed = now() - this.hintStart;
    return 0.5 - 0.5 * Math.cos((elapsed / 520) * Math.PI * 2);
  }

  private startHintLoop(): void {
    this.stopHintLoop();
    if (this.reducedMotion()) {
      // Static highlight, cleared after a beat with no animation.
      this.render();
      this.hintClearTimer = window.setTimeout(() => {
        this.hint = null;
        this.hintClearTimer = null;
        this.render();
      }, HINT_MS);
      return;
    }
    const tick = (): void => {
      if (this.hint === null) {
        this.hintRaf = null;
        return;
      }
      if (now() - this.hintStart >= HINT_MS) {
        this.hint = null;
        this.hintRaf = null;
        this.render();
        return;
      }
      this.render();
      this.hintRaf = requestAnimationFrame(tick);
    };
    this.hintRaf = requestAnimationFrame(tick);
  }

  private stopHintLoop(): void {
    if (this.hintRaf !== null) {
      cancelAnimationFrame(this.hintRaf);
      this.hintRaf = null;
    }
    if (this.hintClearTimer !== null) {
      window.clearTimeout(this.hintClearTimer);
      this.hintClearTimer = null;
    }
  }

  private clearHint(): void {
    this.stopHintLoop();
    this.hint = null;
  }

  // ---- settings ----

  private applySettings(s: Settings): void {
    this.settings = s;
    saveSettings(s);
    this.sfx.setEnabled(s.sound);
    if (s.sound) {
      this.sfx.resume(); // toggled on from within a gesture — prime audio now so it's ready
    }
    this.render();
  }

  private doResetProgress(): void {
    if (this.animator.running) {
      return; // don't swap levels mid-animation (would orphan an in-flight commit)
    }
    resetAllProgress();
    this.loadLevel(1); // persists currentLevel = 1
  }

  // ---- tutorial (§5.5) ----

  private maybeShowTutorial(): void {
    if (loadTutorialSeen()) {
      return;
    }
    try {
      const t = new Tutorial(() => {
        this.tutorial = null;
      });
      this.tutorial = t;
      t.show(this.reducedMotion());
      saveTutorialSeen(true); // shows only once
    } catch {
      // The tutorial is non-essential; never block play (no fail states, §0).
      this.tutorial = null;
    }
  }

  // ---- audio priming ----

  private handleFirstGesture(): void {
    if (this.audioPrimed) {
      return;
    }
    this.audioPrimed = true;
    this.removeGestureListeners();
    this.sfx.resume();
  }

  private removeGestureListeners(): void {
    window.removeEventListener('pointerdown', this.onFirstGesture, true);
    window.removeEventListener('keydown', this.onFirstGesture, true);
    window.removeEventListener('touchstart', this.onFirstGesture, true);
  }

  // ---- helpers ----

  private reducedMotion(): boolean {
    const pref = this.settings.reducedMotion;
    if (pref === 'on') {
      return true;
    }
    if (pref === 'off') {
      return false;
    }
    if (typeof window.matchMedia === 'function') {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    return false;
  }

  private celebrate(): void {
    if (this.reducedMotion()) {
      return; // honor the in-app Reduced-motion setting (CSS @media only covers the OS-level query)
    }
    const el = this.boardWrap;
    if (el === null) {
      return;
    }
    el.classList.remove('celebrate');
    // Force reflow so the animation can replay on consecutive wins.
    void el.offsetWidth;
    el.classList.add('celebrate');
    window.setTimeout(() => el.classList.remove('celebrate'), CELEBRATE_MS);
  }

  private clearNextTimer(): void {
    if (this.nextTimer !== null) {
      window.clearTimeout(this.nextTimer);
      this.nextTimer = null;
    }
  }
}

/** Generate `index`, nudging forward a few seeds if the (extremely rare) verify gate exhausts. */
function safeGenerate(index: number): GeneratedLevel {
  let lastErr: unknown = null;
  for (let i = index; i < index + 6; i++) {
    try {
      return generateLevel(i);
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('generateLevel failed');
}

/**
 * Auto-apply new deploys: with the service worker on `autoUpdate`, a fresh build's worker activates
 * and claims this client, firing `controllerchange`. Reload once (guarded against loops) so the
 * player actually SEES the new version instead of the old cached shell. Without this, an installed
 * PWA can keep serving a stale build for a launch or two.
 */
function installAutoReload(): void {
  if (!('serviceWorker' in navigator)) {
    return;
  }
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) {
      return;
    }
    reloading = true;
    window.location.reload();
  });
  // Force an update check on load AND whenever the app regains focus, instead of waiting for the
  // browser's lazy heuristics. With the worker on autoUpdate (skipWaiting + clientsClaim), a found
  // update activates and fires `controllerchange` → reload. This is what makes a fresh deploy land
  // within seconds of opening the app — no manual cache-clear. Offline: update() just no-ops.
  const checkForUpdate = (): void => {
    void navigator.serviceWorker
      .getRegistration()
      .then((reg) => reg?.update())
      .catch(() => {
        /* offline / not yet registered — ignore */
      });
  };
  void navigator.serviceWorker.ready.then(checkForUpdate).catch(() => {});
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkForUpdate();
    }
  });
}

function boot(): void {
  const canvas = document.getElementById('board');
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error('main: #board canvas not found');
  }
  requestPersistentStorage(); // ask the browser not to evict saved progress
  installAutoReload(); // pull in new deploys without a manual cache-clear
  // Retained on window-less teardown via closure; the App lives for the page lifetime.
  const app = new App(canvas);
  window.addEventListener('pagehide', () => app.dispose(), { once: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
